import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {Tresorerie} from '../../models/Tresorerie/Tresorerie';
import {CreateSoldePayload, Solde} from '../../models/Tresorerie/Solde';
import {CategorieTresorerie} from '../../models/Tresorerie/CategorieTresorerie';
import {RemiseBanque} from '../../models/Tresorerie/RemiseBanque';
import {LigneRemiseBanque} from '../../models/Tresorerie/LigneRemiseBanque';
import {TresorerieFilters} from '../../models/Tresorerie/TresorerieFilters';
import {ChequeTraite} from '../../models/Tresorerie/ChequeTraite';


@Injectable({
    providedIn: 'root'
})
export class TresorerieService {
    private baseUrl = `${environment.tresorerie_url.replace(/\/$/, '')}/api/tresorerie`;

    constructor(private http: HttpClient) {}

    /** Aligné sur le stock : currentUser + stations[0] pour les query obligatoires. */
    private getStationUserIdsFromStorage(): { stationId: number; userId: number } {
        try {
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const userId = Number(currentUser?.id) || 0;
            const station = currentUser?.stations?.[0];
            const stationId =
                Number(station?.station_id ?? station?.id ?? currentUser?.station_id) || 0;
            return { userId, stationId };
        } catch {
            return { userId: 0, stationId: 0 };
        }
    }

    /**
     * GET /api/tresorerie exige user_id (query) ; station_id est ajouté depuis le stockage si absent des params.
     */
    private appendMandatoryTresorerieListParams(
        base: HttpParams,
        filters?: TresorerieFilters
    ): HttpParams {
        const { stationId, userId } = this.getStationUserIdsFromStorage();
        let p = base;
        if (!p.get('station_id') && stationId > 0) {
            p = p.set('station_id', String(stationId));
        }
        const uid =
            filters?.user_id != null && filters.user_id > 0 ? filters.user_id : userId;
        if (uid > 0) {
            p = p.set('user_id', String(uid));
        }
        return p;
    }

    // ============================================
    // OPÉRATIONS DE TRÉSORERIE
    // ============================================

    createTresorerie(tresorerie: Tresorerie): Observable<Tresorerie> {
        return this.http.post<Tresorerie>(this.baseUrl, tresorerie);
    }

    /** Trésorerie par client : GET /api/tresorerie/client/:client_id (optionnel: dateFrom, dateTo) */
    getTresorerieByClientId(clientId: number, filters?: { dateFrom?: string; dateTo?: string }, page: number = 1, limit: number = 500): Observable<{ tresoreries: Tresorerie[]; pagination?: any }> {
        let params = new HttpParams();
        if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
        if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);
        params = params.set('page', page.toString()).set('limit', limit.toString());
        return this.http.get<any>(`${this.baseUrl}/client/${clientId}`, { params }).pipe(
            map(res => ({
                tresoreries: res?.data?.tresoreries || [],
                pagination: res?.data?.pagination
            }))
        );
    }

    /** Trésorerie par fournisseur : GET /api/tresorerie/fournisseur/:fournisseur_id (optionnel: dateFrom, dateTo) */
    getTresorerieByFournisseurId(fournisseurId: number, filters?: { dateFrom?: string; dateTo?: string }, page: number = 1, limit: number = 500): Observable<{ tresoreries: Tresorerie[]; pagination?: any }> {
        let params = new HttpParams();
        if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
        if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);
        params = params.set('page', page.toString()).set('limit', limit.toString());
        return this.http.get<any>(`${this.baseUrl}/fournisseur/${fournisseurId}`, { params }).pipe(
            map(res => ({
                tresoreries: res?.data?.tresoreries || [],
                pagination: res?.data?.pagination
            }))
        );
    }

    getTresorerieListWithPagination(filters?: TresorerieFilters, page: number = 1, limit: number = 1000): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
            if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
            if (filters.type_operation) params = params.set('type_operation', filters.type_operation);
            if (filters.type_transaction) params = params.set('type_transaction', filters.type_transaction);
            if (filters.methode_paiement) params = params.set('methode_paiement', filters.methode_paiement);
            if (filters.categorie_id) params = params.set('categorie_id', filters.categorie_id.toString());
            //  if (filters.statut) params = params.set('statut', filters.statut);
            if (filters.station_id) params = params.set('station_id', filters.station_id.toString());
            if (filters.search) params = params.set('search', filters.search);
            if (filters.solde_type) params = params.set('solde_type', filters.solde_type);
        }
        params = params.set('page', page.toString());
        params = params.set('limit', limit.toString());
        params = this.appendMandatoryTresorerieListParams(params, filters);

        return this.http.get<any>(this.baseUrl, { params });
    }

    getTresorerieList(filters?: TresorerieFilters): Observable<Tresorerie[]> {
        let params = new HttpParams();
        if (filters) {
            if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
            if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
            if (filters.type_operation) params = params.set('type_operation', filters.type_operation);
            if (filters.type_transaction) params = params.set('type_transaction', filters.type_transaction);
            if (filters.methode_paiement) params = params.set('methode_paiement', filters.methode_paiement);
            if (filters.categorie_id) params = params.set('categorie_id', filters.categorie_id.toString());
            if (filters.station_id) params = params.set('station_id', filters.station_id.toString());
            if (filters.search) params = params.set('search', filters.search);
            if (filters.solde_type) params = params.set('solde_type', filters.solde_type);
        }
        return this.http.get<any>(this.baseUrl, { params }).pipe(
            map(response => {
                let operations: Tresorerie[] = [];
                if (Array.isArray(response) && response.length > 0) {
                    if (response[0] && response[0].tresoreries && Array.isArray(response[0].tresoreries)) {
                        operations = response[0].tresoreries;
                    }
                    else if (response[0] && response[0].id !== undefined) {
                        operations = response;
                    }
                    else if (response[0] && typeof response[0] === 'object') {
                        if (response[0].tresoreries && Array.isArray(response[0].tresoreries)) {
                            operations = response[0].tresoreries;
                        }
                    }
                }
                else if (response && response.data) {
                    if (Array.isArray(response.data)) {
                        operations = response.data;
                    } else if (response.data.tresoreries && Array.isArray(response.data.tresoreries)) {
                        operations = response.data.tresoreries;
                    } else if (response.data.operations && Array.isArray(response.data.operations)) {
                        operations = response.data.operations;
                    }
                }
                else if (response && response.tresoreries && Array.isArray(response.tresoreries)) {
                    operations = response.tresoreries;
                }
                else if (response && response.operations && Array.isArray(response.operations)) {
                    operations = response.operations;
                }
                else if (response && response.tresorerie && Array.isArray(response.tresorerie)) {
                    operations = response.tresorerie;
                }
                else if (response && response.success && response.data) {
                    if (Array.isArray(response.data)) {
                        operations = response.data;
                    } else if (response.data.tresoreries && Array.isArray(response.data.tresoreries)) {
                        operations = response.data.tresoreries;
                    } else {
                        operations = [response.data];
                    }
                }

                if (operations.length === 0) {
                    console.warn('Format de réponse inattendu pour les opérations de trésorerie:', response);
                }

                return operations;
            })
        );
    }

    getTresorerieById(id: number): Observable<Tresorerie> {
        return this.http.get<Tresorerie>(`${this.baseUrl}/${id}`);
    }

    updateTresorerie(id: number, tresorerie: Tresorerie): Observable<Tresorerie> {
        return this.http.put<Tresorerie>(`${this.baseUrl}/${id}`, tresorerie);
    }

    /**
     * Supprime une opération de trésorerie.
     * DELETE /api/tresorerie/:id
     * @param id ID de l'opération de trésorerie à supprimer
     * @returns Observable de la réponse (souvent { success: boolean, message?: string })
     */
    deleteTresorerie(id: number): Observable<{ success?: boolean; message?: string }> {
        return this.http.delete<{ success?: boolean; message?: string }>(`${this.baseUrl}/${id}`);
    }

    // ============================================
    // SOLDES
    // ============================================

    getSoldesList(): Observable<Solde[]> {
        return this.http.get<Solde[]>(`${this.baseUrl}/soldes/list`);
    }

    getSoldesByStation(stationId: number): Observable<Solde[]> {
        return this.http.get<any>(`${this.baseUrl}/soldes/station/${stationId}`).pipe(
            map((res) => this.normalizeSoldesListResponse(res))
        );
    }

    /**
     * Soldes filtrés station + utilisateur.
     * GET /api/tresorerie/soldes/station/:station_id/user/:user_id
     */
    getSoldesByStationAndUser(stationId: number, userId: number): Observable<Solde[]> {
        const url = `${this.baseUrl}/soldes/station/${stationId}/user/${userId}`;
        return this.http.get<any>(url).pipe(map((res) => this.normalizeSoldesListResponse(res)));
    }

    /** Normalise la réponse API (tableau direct, data, data.soldes, etc.). */
    private normalizeSoldesListResponse(res: any): Solde[] {
        if (res == null) {
            return [];
        }
        if (Array.isArray(res)) {
            return res as Solde[];
        }
        if (res.data != null) {
            const d = res.data;
            if (Array.isArray(d)) {
                return d as Solde[];
            }
            if (d.soldes && Array.isArray(d.soldes)) {
                return d.soldes as Solde[];
            }
            if (typeof d === 'object') {
                for (const key of Object.keys(d)) {
                    if (Array.isArray(d[key])) {
                        return d[key] as Solde[];
                    }
                }
            }
        }
        if (res.soldes && Array.isArray(res.soldes)) {
            return res.soldes as Solde[];
        }
        return [];
    }

    getSoldeById(id: number): Observable<Solde> {
        return this.http.get<Solde>(`${this.baseUrl}/soldes/${id}`);
    }

    /**
     * Crée une caisse / solde pour une station.
     * POST `{tresorerie_url}/api/tresorerie/soldes`
     */
    createSolde(payload: CreateSoldePayload): Observable<Solde> {
        const url = `${this.baseUrl}/soldes`;
        return this.http.post<any>(url, payload).pipe(
            map((res) => this.normalizePostSoldeResponse(res))
        );
    }

    /** Extrait le solde créé selon les formats de réponse courants (data, solde, objet direct). */
    private normalizePostSoldeResponse(res: any): Solde {
        if (res == null) {
            return res;
        }
        if (res.data != null && typeof res.data === 'object') {
            const d = res.data;
            if (d.solde != null) {
                return d.solde as Solde;
            }
            if (d.soldes != null && Array.isArray(d.soldes) && d.soldes[0]) {
                return d.soldes[0] as Solde;
            }
            if (d.id != null || d.type_solde != null || d.station_id != null) {
                return d as Solde;
            }
            if (d.data != null && typeof d.data === 'object') {
                return this.normalizePostSoldeResponse({ data: d.data });
            }
        }
        if (res.solde != null) {
            return res.solde as Solde;
        }
        if (res.id != null || res.type_solde != null || res.station_id != null) {
            return res as Solde;
        }
        return res as Solde;
    }

    // ============================================
    // CATÉGORIES
    // ============================================


    createCategorie(categorie: CategorieTresorerie): Observable<CategorieTresorerie> {
        return this.http.post<CategorieTresorerie>(`${this.baseUrl}/categories`, categorie);
    }

    /**
     * GET /api/tresorerie/categories — catégories trésorerie.
     * Envoie `station_id` en query lorsqu’il est connu (filtrage côté API si supporté).
     */
    getCategories(stationId: number): Observable<CategorieTresorerie[]> {
        let params = new HttpParams();
        if (stationId > 0) {
            params = params.set('station_id', String(stationId));
        }
        return this.http.get<any>(`${this.baseUrl}/categories`, { params }).pipe(
            map((response) => this.normalizeCategoriesResponse(response))
        );
    }

    private normalizeCategoriesResponse(response: any): CategorieTresorerie[] {
        if (!response) return [];
        if (Array.isArray(response)) return response.map((c: any) => this.toCategorieTresorerie(c));
        if (response.data != null) {
            const d = response.data;
            if (Array.isArray(d)) return d.map((c: any) => this.toCategorieTresorerie(c));
            if (d.categories && Array.isArray(d.categories)) return d.categories.map((c: any) => this.toCategorieTresorerie(c));
            if (d.data && Array.isArray(d.data)) return d.data.map((c: any) => this.toCategorieTresorerie(c));
            if (typeof d === 'object') {
                for (const key of Object.keys(d)) {
                    if (Array.isArray(d[key]) && d[key].length > 0 && (d[key][0].id != null || d[key][0].nom != null || d[key][0].name != null)) {
                        return d[key].map((c: any) => this.toCategorieTresorerie(c));
                    }
                }
            }
            if (d.id != null || d.nom != null) return [this.toCategorieTresorerie(d)];
        }
        if (response.categories && Array.isArray(response.categories)) {
            return response.categories.map((c: any) => this.toCategorieTresorerie(c));
        }
        return [];
    }

    private toCategorieTresorerie(raw: any): CategorieTresorerie {
        return {
            id: raw?.id != null ? Number(raw.id) : undefined,
            nom: raw?.nom ?? raw?.name ?? raw?.libelle ?? raw?.titre ?? '',
            description: raw?.description ?? null,
            type: raw?.type ?? null,
            created_at: raw?.created_at,
            updated_at: raw?.updated_at
        };
    }

    getCategorieById(id: number): Observable<CategorieTresorerie> {
        return this.http.get<CategorieTresorerie>(`${this.baseUrl}/categories/${id}`);
    }


    updateCategorie(id: number, categorie: CategorieTresorerie): Observable<CategorieTresorerie> {
        return this.http.put<CategorieTresorerie>(`${this.baseUrl}/categories/${id}`, categorie);
    }

    deleteCategorie(id: number): Observable<any> {
        return this.http.delete(`${this.baseUrl}/categories/${id}`);
    }

    // ============================================
    // REMISES EN BANQUE
    // ============================================


    createRemiseBanque(remise: RemiseBanque): Observable<RemiseBanque> {
        return this.http.post<RemiseBanque>(`${this.baseUrl}/remises-banque`, remise);
    }


    getRemisesBanque(): Observable<RemiseBanque[]> {
        return this.http.get<RemiseBanque[]>(`${this.baseUrl}/remises-banque`);
    }


    getRemiseBanqueById(id: number): Observable<RemiseBanque> {
        return this.http.get<RemiseBanque>(`${this.baseUrl}/remises-banque/${id}`);
    }


    updateRemiseBanque(id: number, remise: RemiseBanque): Observable<RemiseBanque> {
        return this.http.put<RemiseBanque>(`${this.baseUrl}/remises-banque/${id}`, remise);
    }


    rejeterLigneRemise(ligneId: number): Observable<any> {
        return this.http.post(`${this.baseUrl}/remises-banque/lignes/${ligneId}/rejeter`, {});
    }

    getTresorerieByCompteBancaire(compteBancaireId: number): Observable<Tresorerie[]> {
        const url = `${this.baseUrl}/compte-bancaire/${compteBancaireId}`;
        
        return new Observable(observer => {
            let allOperations: Tresorerie[] = [];
            let currentPage = 1;
            const limit = 1000;

            const fetchPage = () => {
                let params = new HttpParams();
                params = params.set('page', currentPage.toString());
                params = params.set('limit', limit.toString());

                this.http.get<any>(url, { params }).subscribe({
                    next: (response) => {
                        let pageOperations: Tresorerie[] = [];
                        if (response && response.success && response.data && response.data.tresoreries && Array.isArray(response.data.tresoreries)) {
                            pageOperations = response.data.tresoreries;
                        }
                        else if (response && response.data && response.data.tresoreries && Array.isArray(response.data.tresoreries)) {
                            pageOperations = response.data.tresoreries;
                        }
                        else if (response && response.data && Array.isArray(response.data)) {
                            pageOperations = response.data;
                        }
                        else if (Array.isArray(response) && response.length > 0 && response[0].id !== undefined) {
                            pageOperations = response;
                        }
                        else if (response && response.tresoreries && Array.isArray(response.tresoreries)) {
                            pageOperations = response.tresoreries;
                        }
                        else if (response && response.operations && Array.isArray(response.operations)) {
                            pageOperations = response.operations;
                        }

                        allOperations = [...allOperations, ...pageOperations];
                        let totalPages = 1;
                        if (response && response.data && response.data.pagination) {
                            totalPages = response.data.pagination.pages || 1;
                        } else if (response && response.pagination) {
                            totalPages = response.pagination.pages || 1;
                        } else if (response && response.meta) {
                            totalPages = response.meta.pages || response.meta.nb_total_pages || 1;
                        }


                        if (currentPage < totalPages && pageOperations.length > 0) {
                            currentPage++;
                            fetchPage();
                        } else {
                            observer.next(allOperations);
                            observer.complete();
                        }
                    },
                    error: (error) => {
                        observer.error(error);
                    }
                });
            };

            fetchPage();
        });
    }


    getRemisesBanqueByCompteBancaire(compteBancaireId: number): Observable<RemiseBanque[]> {
        const url = `${this.baseUrl}/remises-banque/compte-bancaire/${compteBancaireId}`;
        
        return new Observable(observer => {
            let allRemises: RemiseBanque[] = [];
            let currentPage = 1;
            const limit = 1000;

            const fetchPage = () => {
                let params = new HttpParams();
                params = params.set('page', currentPage.toString());
                params = params.set('limit', limit.toString());

                this.http.get<any>(url, { params }).subscribe({
                    next: (response) => {
                        
                        let pageRemises: RemiseBanque[] = [];
                        if (response && response.success && response.data && response.data.remises_banque && Array.isArray(response.data.remises_banque)) {
                            pageRemises = response.data.remises_banque;
                        }
                        else if (response && response.data && response.data.remises_banque && Array.isArray(response.data.remises_banque)) {
                            pageRemises = response.data.remises_banque;
                        }
                        else if (response && response.data && response.data.remises && Array.isArray(response.data.remises)) {
                            pageRemises = response.data.remises;
                        }
                        else if (response && response.data && Array.isArray(response.data)) {
                            pageRemises = response.data;
                        }
                        else if (Array.isArray(response)) {
                            pageRemises = response;
                        }
                        else if (response && response.remises_banque && Array.isArray(response.remises_banque)) {
                            pageRemises = response.remises_banque;
                        }
                        else if (response && response.remises && Array.isArray(response.remises)) {
                            pageRemises = response.remises;
                        }

                        allRemises = [...allRemises, ...pageRemises];
                        let totalPages = 1;
                        if (response && response.data && response.data.pagination) {
                            totalPages = response.data.pagination.pages || 1;
                        } else if (response && response.pagination) {
                            totalPages = response.pagination.pages || 1;
                        } else if (response && response.meta) {
                            totalPages = response.meta.pages || response.meta.nb_total_pages || 1;
                        }
                        if (currentPage < totalPages && pageRemises.length > 0) {
                            currentPage++;
                            fetchPage();
                        } else {
                            observer.next(allRemises);
                            observer.complete();
                        }
                    },
                    error: (error) => {
                        observer.error(error);
                    }
                });
            };

            fetchPage();
        });
    }

    // ============================================
    // AVANCES EN ESPÈCE
    // ============================================

    createAvanceEspeceClient(clientId: number, stationId: number, data: { solde_avance?: number; note_avance?: string; user_id: number }): Observable<any> {
        const url = `${this.baseUrl}/avances-espece/client/${clientId}/station/${stationId}`;
        return this.http.patch<any>(url, data);
    }

    getAvancesEspeceClient(clientId: number, stationId?: number): Observable<Tresorerie[]> {
        let params = new HttpParams();
        params = params.set('type_operation', 'avance_espece');
        params = params.set('client_id', clientId.toString());
       // params = params.set('statut', 'valide');
        params = params.set('statut', 'actif');
        if (stationId) {
            params = params.set('station_id', stationId.toString());
        }
        params = this.appendMandatoryTresorerieListParams(params);

        const url = `${this.baseUrl}`;

        return this.http.get<any>(url, { params }).pipe(
            map(response => {
                let operations: Tresorerie[] = [];
                if (Array.isArray(response)) {
                    operations = response;
                } else if (response && response.data) {
                    if (Array.isArray(response.data)) {
                        operations = response.data;
                    } else if (response.data.tresoreries && Array.isArray(response.data.tresoreries)) {
                        operations = response.data.tresoreries;
                    } else if (response.data.operations && Array.isArray(response.data.operations)) {
                        operations = response.data.operations;
                    } else {
                    }
                } else if (response && response.tresoreries && Array.isArray(response.tresoreries)) {
                    operations = response.tresoreries;
                } else if (response && response.operations && Array.isArray(response.operations)) {
                    operations = response.operations;
                }
                const operationsFiltered = operations.filter((op) => {
                    const matchesType = op.type_operation === 'avance_espece';
                    const matchesClient =
                        op.client_id != null && Number(op.client_id) === Number(clientId);
                    const matchesStatut =
                        op.statut == null ||
                        op.statut === 'actif' ||
                        op.statut === 'valide';

                    return matchesType && matchesClient && matchesStatut;
                });

                console.log('📦 [TresorerieService] Opérations filtrées:', operationsFiltered.length);
                return operationsFiltered;
            })
        );
    }

    // ============================================
    // CHÈQUES ET TRAITES
    // ============================================


    createChequeTraite(chequeTraite: ChequeTraite): Observable<any> {
        const url = `${this.baseUrl}/cheques-traites`;
        return this.http.post<any>(url, chequeTraite);
    }


    getChequesTraites(filters?: any): Observable<any> {
        const url = `${this.baseUrl}/cheques-traites`;
        let params = new HttpParams();
        
        if (filters) {
            if (filters.station_id) params = params.set('station_id', filters.station_id.toString());
            if (filters.type_operation) params = params.set('type_operation', filters.type_operation);
            if (filters.type_paiement) params = params.set('type_paiement', filters.type_paiement);
            if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
            if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
            if (filters.page) params = params.set('page', filters.page.toString());
            if (filters.limit) params = params.set('limit', filters.limit.toString());
        }
        return this.http.get<any>(url, { params });
    }
    deleteChequeTraite(id: number): Observable<any> {
        const url = `${this.baseUrl}/cheques-traites/${id}`;
        return this.http.delete<any>(url);
    }

    updateChequeTraiteStatut(chequeTraiteId: number, statut: string): Observable<any> {
        const url = `${this.baseUrl}/cheques-traites/${chequeTraiteId}/statut`;
        return this.http.patch<any>(url, { statut });
    }
}
